import { useState, type JSX } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { useAuth } from '@hooks/auth';
import { resolveSignInError } from '../lib/signInError';

interface SignInFormProps {
  onSuccess: () => void;
}

export function SignInForm({ onSuccess }: Readonly<SignInFormProps>): JSX.Element {
  const { t } = useTranslation('auth');
  const { signIn, isSigningIn, signInError } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const signInErrorMessage = resolveSignInError(signInError, t);

  const schema = z.object({
    username: z
      .string()
      .min(1, t('signIn.errors.required'))
      .email(t('signIn.errors.invalidEmail')),
    password: z.string().min(1, t('signIn.errors.required')),
  });

  type FormValues = z.infer<typeof schema>;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { username: '', password: '' },
  });

  const onSubmit = handleSubmit(async ({ username, password }) => {
    const ok = await signIn(username, password);
    if (ok) onSuccess();
  });

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4" data-testid="signin-form" noValidate>
      <div className="flex flex-col gap-1.5">
        <label
          htmlFor="signin-username"
          className="text-sm font-medium"
          data-testid="signin-username-input-label"
        >
          {t('signIn.username')}
        </label>
        <Input
          id="signin-username"
          type="email"
          autoComplete="email"
          autoFocus
          placeholder={t('signIn.usernamePlaceholder')}
          aria-invalid={Boolean(errors.username)}
          aria-describedby={errors.username ? 'signin-username-error' : undefined}
          data-testid="signin-username-input"
          {...register('username')}
        />
        {errors.username && (
          <p
            id="signin-username-error"
            className="text-xs text-destructive"
            data-testid="signin-username-input-error"
          >
            {errors.username.message}
          </p>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <label
          htmlFor="signin-password"
          className="text-sm font-medium"
          data-testid="signin-password-input-label"
        >
          {t('signIn.password')}
        </label>
        <div className="relative">
          <Input
            id="signin-password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            placeholder={t('signIn.passwordPlaceholder')}
            aria-invalid={Boolean(errors.password)}
            aria-describedby={errors.password ? 'signin-password-error' : undefined}
            // data-sentry-mask: Sentry replay also masks all inputs globally,
            // but we set the attribute explicitly so any Sentry config change
            // doesn't accidentally expose the password field.
            data-sentry-mask="true"
            data-testid="signin-password-input"
            className="pr-10"
            {...register('password')}
          />
          {/* Show/hide password toggle — parity with the legacy login's eye icon. */}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => setShowPassword((v) => !v)}
            aria-label={showPassword ? t('signIn.hidePassword') : t('signIn.showPassword')}
            aria-pressed={showPassword}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            data-testid="signin-password-toggle"
          >
            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </Button>
        </div>
        {errors.password && (
          <p
            id="signin-password-error"
            className="text-xs text-destructive"
            data-testid="signin-password-input-error"
          >
            {errors.password.message}
          </p>
        )}
      </div>

      {signInErrorMessage && (
        <div
          role="alert"
          className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          data-testid="signin-server-error"
        >
          {signInErrorMessage}
        </div>
      )}

      <Button
        type="submit"
        className="w-full"
        disabled={isSigningIn}
        data-testid="signin-submit-button"
      >
        {isSigningIn ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            {t('signIn.submitting')}
          </>
        ) : (
          t('signIn.submit')
        )}
      </Button>
    </form>
  );
}
